import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { CATEGORIES } from '../../lib/categories';
import { CATEGORY_THUMBNAILS } from '../../lib/categoryAssets';
import { useTheme } from '../../contexts/ThemeContext';

interface CategoryBrowserProps {
    onSelectCategory: (catId: string, subCatId?: string, typeId?: string) => void;
}

const CategoryBrowser: React.FC<CategoryBrowserProps> = ({ onSelectCategory }) => {
    const { t } = useTranslation('marketplace');
    const { theme } = useTheme();
    const [activeMainCat, setActiveMainCat] = useState(CATEGORIES[0]?.id || '');

    const mainCategory = CATEGORIES.find(c => c.id === activeMainCat);

    return (
        <div className="flex h-[calc(100vh-140px)] bg-white dark:bg-gray-950 overflow-hidden animate-in fade-in duration-500">
            {/* Sidebar - Main Categories */}
            <div className="w-24 md:w-32 bg-gray-50/50 dark:bg-gray-900/50 border-r border-gray-100 dark:border-gray-800 overflow-y-auto scrollbar-hide">
                {CATEGORIES.map((cat) => (
                    <button
                        key={cat.id}
                        onClick={() => setActiveMainCat(cat.id)}
                        className={`w-full py-6 px-2 flex flex-col items-center gap-2 transition-all relative ${activeMainCat === cat.id ? 'bg-white dark:bg-gray-950' : 'hover:bg-gray-100 dark:hover:bg-gray-800/50'
                            }`}
                    >
                        {activeMainCat === cat.id && (
                            <motion.div
                                layoutId="activeCategoryIndicator"
                                className="absolute left-0 top-0 bottom-0 w-1 bg-blue-600 dark:bg-blue-500"
                            />
                        )}
                        <span className={`text-[10px] font-black uppercase tracking-tighter text-center leading-tight transition-colors duration-200 ${activeMainCat === cat.id ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-gray-600'}`}>
                            {cat.label}
                        </span>
                    </button>
                ))}
            </div>

            {/* Main Content - Sub Categories Grid */}
            <div className="flex-1 overflow-y-auto px-4 py-6 scrollbar-hide bg-white dark:bg-gray-950">
                <div className="space-y-6">
                    <div className="flex items-center justify-between">
                        <h2 className="text-xl font-black text-gray-900 dark:text-white">{mainCategory?.label}</h2>
                        <button
                            onClick={() => onSelectCategory(activeMainCat)}
                            className="text-[10px] font-black uppercase tracking-widest text-blue-600 dark:text-blue-400 hover:opacity-80 transition-opacity"
                        >
                            {t('categoryBrowser.seeAll')}
                        </button>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        {mainCategory?.subCategories.map((sub) => (
                            <motion.button
                                key={sub.id}
                                whileTap={{ scale: 0.95 }}
                                onClick={() => onSelectCategory(activeMainCat, sub.id)}
                                className="flex flex-col items-center group"
                            >
                                <div className="w-full aspect-square rounded-2xl bg-gray-50 dark:bg-gray-900 border border-gray-100 dark:border-gray-800 overflow-hidden mb-2 shadow-sm group-hover:shadow-none transition-all relative">
                                    <AnimatePresence mode="wait">
                                        <motion.img
                                            key={`${sub.id}-${theme}`}
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            exit={{ opacity: 0 }}
                                            transition={{ duration: 0.3 }}
                                            src={CATEGORY_THUMBNAILS[sub.id]?.[theme] || `https://source.unsplash.com/400x400/?${sub.id || 'product'}`}
                                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                                            alt={sub.label}
                                        />
                                    </AnimatePresence>
                                </div>
                                <span className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase text-center group-hover:text-gray-900 dark:group-hover:text-gray-25 transition-colors px-1">
                                    {sub.label}
                                </span>
                            </motion.button>
                        ))}
                    </div>

                    {/* Special Promo Section in Grid */}
                    <motion.div
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => onSelectCategory(activeMainCat)}
                        className="mt-8 p-6 rounded-[2rem] bg-gradient-to-br from-gray-900 to-gray-800 dark:from-gray-800 dark:to-gray-900 text-white shadow-xl relative overflow-hidden group cursor-pointer border border-gray-100/10 dark:border-gray-700/50 transition-all"
                    >
                        <div className="relative z-10 space-y-2">
                            <span className="text-[9px] font-black uppercase tracking-[0.2em] opacity-60">
                                {t('categoryBrowser.weeklyHighlights')}
                            </span>
                            <h3 className="text-lg font-black leading-tight">
                                {t('categoryBrowser.bestOf', { category: mainCategory?.label })}
                            </h3>
                        </div>
                        <div className="absolute right-[-20px] bottom-[-20px] w-32 h-32 bg-white/10 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-700" />
                    </motion.div>
                </div>
            </div>
        </div>
    );
};

export default CategoryBrowser;
